export type UsageScope = 'selected_business_units' | 'organisation_wide' | 'unknown'

export type UsageScopeState = {
  scope: UsageScope
  businessUnitIds: string[]
}

type DraftLike = Record<string, unknown>

function stringValue(value: unknown) {
  return typeof value === 'string' ? value : ''
}

function stringIds(value: unknown) {
  if (!Array.isArray(value)) return []
  return value.filter((item): item is string => typeof item === 'string' && item.length > 0)
}

function unique(ids: string[]) {
  return [...new Set(ids)]
}

/**
 * Return the one canonical form state for usage scope.
 *
 * Accountability is intentionally not an input. Missing legacy usage fields are
 * represented as unknown, while an explicitly present empty usage-ID array is
 * retained as an intentional selected-area form state so validation can surface
 * it without silently restoring the accountable area.
 */
export function normaliseUsageScopeState(data: DraftLike, activeBusinessUnitIds?: readonly string[]): UsageScopeState {
  const rawScope = stringValue(data.usageScope)
  const hasExplicitIds = Object.prototype.hasOwnProperty.call(data, 'usageBusinessUnitIds') && Array.isArray(data.usageBusinessUnitIds)
  const active = activeBusinessUnitIds ? new Set(activeBusinessUnitIds) : null
  const selectedIds = unique(stringIds(data.usageBusinessUnitIds)).filter((id) => !active || active.has(id))

  if (rawScope === 'organisation_wide' || rawScope === 'unknown') {
    return { scope: rawScope, businessUnitIds: [] }
  }

  if (rawScope === 'selected_business_units') {
    return { scope: 'selected_business_units', businessUnitIds: selectedIds }
  }

  if (hasExplicitIds) {
    return { scope: 'selected_business_units', businessUnitIds: selectedIds }
  }

  return { scope: 'unknown', businessUnitIds: [] }
}

export function usageScopeIsComplete(state: UsageScopeState) {
  return state.scope !== 'selected_business_units' || state.businessUnitIds.length > 0
}
